'use client';
import { useCallback, useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { ShowMoreButton } from '@/components/ui/ShowMoreButton/ShowMoreButton';
import { Pagination } from '@/components/features/Pagination/Pagination';
import { NoResults } from '@/components/features/NoResults/NoResults';
import { PhoneLink } from '@/components/ui/PhoneLink/PhoneLink';
import { Loader } from '@/components/ui/Loader/Loader';
import { DoctorCard } from '@/components';
import { getDoctorsList } from '@/api/doctors/doctors';
import { DoctorItem } from '@/api/doctors/doctors.types';
import s from './DoctorsList.module.scss';

interface Props {
  currentPage: number;
  mainPhone: string | null;
  specialization: string;
  query: string;
}

const PER_PAGE = 12;

export const DoctorsList = ({ currentPage, mainPhone, specialization, query }: Props) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { replace } = useRouter();
  const [cards, setCards] = useState<DoctorItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.delete('show_more');
    replace(`${pathname}?${params.toString()}`, { scroll: false });
  }, []);

  useEffect(() => {
    const fetchDoctors = async () => {
      if (searchParams.get('show_more') === 'true') return;

      setIsLoading(true);

      const doctors = await getDoctorsList({
        specialisation_id__in: specialization ? JSON.parse(specialization).id : undefined,
        page: currentPage,
        search: query,
        per_page: PER_PAGE,
      });

      if (!doctors) {
        setIsLoading(false);
        return;
      }

      setCards(doctors.items);
      setTotalPages(doctors.pages);
      setIsLoading(false);
    };

    fetchDoctors();
  }, [searchParams]);

  const showMore = useCallback(async () => {
    if (currentPage >= totalPages) return;

    const params = new URLSearchParams(searchParams);
    const nextPage = currentPage + 1;
    params.set('show_more', String(true));
    params.set('page', String(nextPage));

    const doctors = await getDoctorsList({
      specialisation_id__in: specialization ? JSON.parse(specialization).id : undefined,
      page: nextPage,
      search: query,
      per_page: PER_PAGE,
    });

    setCards((prev) => [...prev, ...doctors.items]);
    replace(`${pathname}?${params.toString()}`, { scroll: false });
  }, [totalPages, currentPage]);

  return (
    <>
      {isLoading && <Loader fixed />}
      <section className={s.doctors}>
        <div className="custom-container">
          {cards.length > 0 ? (
            <div className={s.doctorsList}>
              {cards.map((doctor) => (
                <DoctorCard
                  key={doctor.id}
                  id={doctor.id}
                  photo={doctor.photo}
                  experience={doctor.experience}
                  lastName={doctor.last_name}
                  firstName={doctor.first_name}
                  middleName={doctor.middle_name}
                  specialisations={doctor.specialisations}
                  category={doctor.category?.title}
                />))}
            </div>
          ) : (
            <NoResults>
              <p>
                Извините, но запрашиваемые вами врачи не найдены.<br />
                Пожалуйста, измените условия поиска или позвоните нам по телефону <PhoneLink phone={mainPhone} codeIsFour />.
              </p>
            </NoResults>
          )}
          {cards.length > 0 && currentPage < totalPages && (
            <ShowMoreButton
              onClick={showMore}
              disabled={totalPages < 2}
              className={s.doctors__showMore}
            />
          )}
          <Pagination totalPages={totalPages} className={s.doctors__pagination} />
        </div>
      </section>
    </>
  );
};
